
import { supabase } from '../lib/supabase';
import { getStaffSessionBearer, isStaffSessionJwt, jwtPayload } from './supabaseStorageAuth';

const ANON_KEY = () => String(import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim().replace(/^['"]|['"]$/g, '')
const SUPABASE_URL = () => String(import.meta.env.VITE_SUPABASE_URL || '').trim()

// ─── JWT helpers ──────────────────────────────────────────────────────────────

function jwtExpiryMs(token: string): number | null {
    const p = jwtPayload(token)
    return typeof p?.exp === 'number' ? (p.exp as number) * 1000 : null
}

function isJwt(value: string): boolean {
    return value.split('.').length === 3
}

function projectRef(supabaseUrl: string): string | null {
    try { return new URL(supabaseUrl).hostname.split('.')[0] || null } catch { return null }
}

// ─── Main header builder ──────────────────────────────────────────────────────

/**
 * Build headers for an Edge Function call.
 *
 * Rules:
 *  1. Always send  `apikey: <anon-key>`   (gateway needs it for rate-limiting).
 *  2. Always send  `Authorization: Bearer ...`.
 *     - Prefer a fresh, valid user access_token (same project).
 *     - Fall back to the anon key — which IS a valid JWT and the gateway accepts it.
 *  3. Never send an expired or wrong-project user token; fall back to anon in those cases.
 */
export async function getEdgeFunctionAuthHeaders(): Promise<Record<string, string>> {
    const anonKey = ANON_KEY()
    const expectedRef = projectRef(SUPABASE_URL()) ?? (isJwt(anonKey) ? String(jwtPayload(anonKey)?.ref ?? '') : '')

    const staffBearer = getStaffSessionBearer()
    let bearer = staffBearer || anonKey

    if (!staffBearer) {
        try {
            const { data: { session } } = await supabase.auth.getSession()
            let token = session?.access_token?.trim().replace(/^['"]|['"]$/g, '') ?? ''

            if (token && isJwt(token)) {
                const ref = String(jwtPayload(token)?.ref ?? '')
                if (expectedRef && ref && ref !== expectedRef) token = ''
            }

            if (token && isJwt(token) && !isStaffSessionJwt(token)) {
                const expMs = jwtExpiryMs(token)
                const nearExpiry = expMs != null && expMs <= Date.now() + 60_000

                if (nearExpiry || expMs == null) {
                    const { data: refreshed } = await supabase.auth.refreshSession()
                    token = refreshed.session?.access_token?.trim() ?? ''
                    if (token && isJwt(token)) {
                        const ref = String(jwtPayload(token)?.ref ?? '')
                        if (expectedRef && ref && ref !== expectedRef) token = ''
                    }
                }
            }

            if (token && isJwt(token) && !isStaffSessionJwt(token)) {
                const expMs = jwtExpiryMs(token)
                if (expMs != null && expMs > Date.now()) {
                    bearer = token
                }
            }
        } catch {
            // On any auth error just fall through to anon / staff bearer
        }
    }

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': anonKey,
    }
    if (isJwt(bearer)) {
        headers['Authorization'] = `Bearer ${bearer}`
    }
    return headers
}

/**
 * For actions that run purely via the service role inside the function (send-otp,
 * verify-otp, get-system-settings with bypass) we still need a valid Authorization
 * header — but we don't need a *user* JWT. Send the anon key as the bearer if it's a JWT.
 */
function anonOnlyHeaders(): Record<string, string> {
    const key = ANON_KEY()
    return {
        'Content-Type': 'application/json',
        'apikey': key,
        Authorization: `Bearer ${key}`,
    }
}

/**
 * Staff login must use anon JWT only — never a stale staff/tenant bearer (gateway returns 401).
 */
export async function invokeStaffLogin(
    body: Record<string, unknown>
): Promise<{ data: Record<string, unknown> | null; error: Error | null }> {
    const base = SUPABASE_URL().replace(/\/$/, '')
    const url = `${base}/functions/v1/staff-login`
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: anonOnlyHeaders(),
            body: JSON.stringify(body ?? {}),
        })
        const text = await response.text()
        let data: Record<string, unknown> | null = null
        try {
            data = text ? JSON.parse(text) as Record<string, unknown> : null
        } catch {
            data = { raw: text }
        }
        if (!response.ok) {
            const msg =
                data && typeof data.error === 'string'
                    ? data.error
                    : text || response.statusText || `HTTP ${response.status}`
            console.error(`[invokeStaffLogin] ${response.status} ${url}: ${msg}`)
            return { data, error: new Error(msg) }
        }
        if (data?.error) {
            return { data, error: new Error(String(data.error)) }
        }
        return { data, error: null }
    } catch (e) {
        return { data: null, error: e instanceof Error ? e : new Error(String(e)) }
    }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * POST to `/functions/v1/{name}` with {@link getEdgeFunctionAuthHeaders}.
 * Prefer this over `supabase.functions.invoke`: the Edge gateway often returns **401 Invalid JWT**
 * when `invoke` attaches a stale or malformed user token; this path refreshes near expiry and
 * falls back to the anon JWT only when appropriate (functions that need a *user* JWT must validate
 * in code — use {@link invokeEdgeFunctionWithUserJwt} instead).
 */
export async function invokeEdgeFunction(
    functionName: string,
    body: unknown
): Promise<{ data: unknown | null; error: Error | null }> {
    const base = SUPABASE_URL().replace(/\/$/, '')
    const url = `${base}/functions/v1/${encodeURIComponent(functionName)}`
    try {
        const headers = await getEdgeFunctionAuthHeaders()
        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(body ?? {}),
        })
        const text = await response.text()
        let data: unknown = null
        try {
            data = text ? JSON.parse(text) : null
        } catch {
            data = { raw: text }
        }
        if (!response.ok) {
            const msg =
                typeof data === 'object' && data !== null && 'error' in data
                    ? String((data as { error?: string }).error)
                    : text || response.statusText
            return { data, error: new Error(`Edge Function ${response.status}: ${msg}`) }
        }
        return { data, error: null }
    } catch (e) {
        return { data: null, error: e instanceof Error ? e : new Error(String(e)) }
    }
}

/**
 * Edge Function calls that must run as the signed-in user (gateway + `auth.getUser(jwt)`).
 * Sends a **user** access token only (refreshes if near expiry); fails if not logged in.
 */
export async function invokeEdgeFunctionWithUserJwt(
    functionName: string,
    body: unknown
): Promise<{ data: unknown | null; error: Error | null }> {
    const anonKey = ANON_KEY()
    const base = SUPABASE_URL().replace(/\/$/, '')
    const url = `${base}/functions/v1/${encodeURIComponent(functionName)}`

    try {
        const { data: { session } } = await supabase.auth.getSession()
        let token = session?.access_token?.trim().replace(/^['"]|['"]$/g, '') ?? ''

        const staffBearer = getStaffSessionBearer();
        if (staffBearer && (sessionStorage.getItem('impersonate_tenant') || localStorage.getItem('impersonate_tenant') || !token || token === anonKey)) {
           token = staffBearer
        }

        if (token && isJwt(token)) {
            const expMs = jwtExpiryMs(token)
            if (expMs != null && expMs <= Date.now() + 60_000) {
                const { data: refreshed } = await supabase.auth.refreshSession()
                token = refreshed.session?.access_token?.trim() ?? token
            }
        }

        if (!token || !isJwt(token)) {
            return { data: null, error: new Error('You must be signed in to complete this action.') }
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'apikey': anonKey,
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body ?? {}),
        })
        const text = await response.text()
        let data: unknown = null
        try {
            data = text ? JSON.parse(text) : null
        } catch {
            data = { raw: text }
        }
        if (!response.ok) {
            const msg =
                typeof data === 'object' && data !== null && 'error' in data
                    ? String((data as { error?: string }).error)
                    : text || response.statusText
            return { data, error: new Error(`Edge Function ${response.status}: ${msg}`) }
        }
        return { data, error: null }
    } catch (e) {
        return { data: null, error: e instanceof Error ? e : new Error(String(e)) }
    }
}

/**
 * Same pattern as `connect-northstar-membership` / admin-actions: **anon** JWT in `Authorization`
 * (Edge gateway accepts it) and the real user access token in JSON `userToken` for
 * `auth.getUser()` inside the function. Avoids gateway **401 Invalid JWT** on user tokens.
 */
export async function invokeEdgeFunctionWithUserTokenInBody(
    functionName: string,
    body: Record<string, unknown>
): Promise<{ data: unknown | null; error: Error | null }> {
    const anonKey = ANON_KEY()
    const base = SUPABASE_URL().replace(/\/$/, '')
    const url = `${base}/functions/v1/${encodeURIComponent(functionName)}`
    try {
        const { data: { session } } = await supabase.auth.getSession()
        let token = session?.access_token?.trim().replace(/^['"]|['"]$/g, '') ?? ''

        if (token && isJwt(token)) {
            const expMs = jwtExpiryMs(token)
            if (expMs != null && expMs <= Date.now() + 60_000) {
                const { data: refreshed } = await supabase.auth.refreshSession()
                token = refreshed.session?.access_token?.trim() ?? token
            }
        }

        if (!token || !isJwt(token)) {
            return { data: null, error: new Error('You must be signed in to complete this action.') }
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'apikey': anonKey,
                Authorization: `Bearer ${anonKey}`,
            },
            body: JSON.stringify({ ...body, userToken: token }),
        })
        const text = await response.text()
        let data: unknown = null
        try {
            data = text ? JSON.parse(text) : null
        } catch {
            data = { raw: text }
        }
        if (!response.ok) {
            const msg =
                typeof data === 'object' && data !== null && 'error' in data
                    ? String((data as { error?: string }).error)
                    : text || response.statusText
            return { data, error: new Error(`Edge Function ${response.status}: ${msg}`) }
        }
        return { data, error: null }
    } catch (e) {
        return { data: null, error: e instanceof Error ? e : new Error(String(e)) }
    }
}

export async function invokeAdminAction(payload: any) {
    const url = `${SUPABASE_URL()}/functions/v1/admin-actions`
    const action = String(payload?.action || '').trim().toLowerCase()
    const anonKey = ANON_KEY()

    try {
        const bodyPayload: Record<string, unknown> = { ...payload }
        delete bodyPayload.bypassSecret

        const isAnonAction = action === 'send-otp' || action === 'verify-otp'

        if (!isAnonAction && !bodyPayload.userToken) {
            const staffBearer = getStaffSessionBearer()
            const { data: { session } } = await supabase.auth.getSession()
            let token = session?.access_token?.trim().replace(/^['"]|['"]$/g, '') ?? ''
            if (staffBearer && (sessionStorage.getItem('impersonate_tenant') || localStorage.getItem('impersonate_tenant') || !token || token === anonKey)) {
                token = staffBearer
            }
            if (token && isJwt(token) && !isStaffSessionJwt(token)) {
                const expMs = jwtExpiryMs(token)
                if (expMs != null && expMs <= Date.now() + 60_000) {
                    const { data: refreshed } = await supabase.auth.refreshSession()
                    token = refreshed.session?.access_token?.trim() ?? token
                }
            }
            if (token) {
                bodyPayload.userToken = token
            }
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: anonOnlyHeaders(),
            body: JSON.stringify(bodyPayload),
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Edge Function responded with ${response.status}: ${errorText}`)
        }

        const data = await response.json()
        if (data?.error) throw new Error(data.error)
        return { data, error: null }
    } catch (err: any) {
        console.error(`[invokeAdminAction] Failed calling ${payload?.action}:`, err.message)
        return { data: null, error: err }
    }
}
